// Generate random match input data for admin testing
import { PlayerDoubleMatch, PlayerMatch } from "../interfaces";
import { Player } from "../interfaces/Team";

// Helper: get a random player from a list, optionally excluding some IDs
export function getRandomPlayer(players: Player[], excludeIds: number[] = []) {
  const filtered = players.filter((p) => !excludeIds.includes(p.id));
  if (!filtered.length) return null;
  return filtered[Math.floor(Math.random() * filtered.length)];
}

// Helper: get 3 random leg values (9-100 as string), optionally force all 3 filled or guarantee at least one blank
export function getRandomLegs(forceAllFilled = false): [string, string, string] {
  if (forceAllFilled) {
    return Array.from({ length: 3 }, () => String(Math.floor(Math.random() * (100 - 9 + 1)) + 9)) as [string, string, string];
  }
  // Generate 3 random numbers
  let legs = Array.from({ length: 3 }, () => String(Math.floor(Math.random() * (100 - 9 + 1)) + 9));
  // Explicitly blank out 1 or 2 legs (never 0, never 3)
  const blankCount = Math.random() < 0.5 ? 1 : 2;
  const blankIndexes: number[] = [];
  for (let i = 0; i < blankCount; i++) {
    let idx;
    do {
      idx = Math.floor(Math.random() * 3);
    } while (blankIndexes.includes(idx));
    blankIndexes.push(idx);
  }
  legs = legs.map((val, idx) => (blankIndexes.includes(idx) ? "" : val));
  return legs as [string, string, string];
}

// Helper: generate a pair of home/away legs, at least one of which is fully filled, and never both
export function getRandomLegPair(): [ [string, string, string], [string, string, string] ] {
  // Randomly decide if home or away is the full one
  const homeFull = Math.random() < 0.5;
  let home: [string, string, string];
  let away: [string, string, string];
  if (homeFull) {
    home = getRandomLegs(true);
    away = getRandomLegs(false);
    return [home, away];
  } else {
    away = getRandomLegs(true);
    home = getRandomLegs(false);
    return [home, away];
  }
}

// Generate unique singles assignments (no player appears more than once)
export function generateRandomSingles(
  playerMatches: PlayerMatch[],
  homePlayers: Player[],
  awayPlayers: Player[]
): { home: Player | null; away: Player | null }[] {
  const availableHome = [...homePlayers];
  const availableAway = [...awayPlayers];
  return playerMatches.map(() => {
    const homeIdx = Math.floor(Math.random() * availableHome.length);
    const home = availableHome.splice(homeIdx, 1)[0] || null;
    const filteredAway = availableAway.filter((p) => p.id !== home?.id);
    const awayIdx = Math.floor(Math.random() * filteredAway.length);
    const away = filteredAway.splice(awayIdx, 1)[0] || null;
    if (away) {
      const idx = availableAway.findIndex((p) => p.id === away.id);
      if (idx !== -1) availableAway.splice(idx, 1);
    }
    return { home, away };
  });
}

// Generate unique doubles assignments (all 4 players unique per match)
export function generateRandomDoubles(
  playerDoubleMatches: PlayerDoubleMatch[],
  homePlayers: Player[],
  awayPlayers: Player[]
): Array<{
  home_player_1: Player | null;
  home_player_2: Player | null;
  away_player_1: Player | null;
  away_player_2: Player | null;
}> {
  return playerDoubleMatches.map(() => {
    const home1 = getRandomPlayer(homePlayers);
    const home2 = getRandomPlayer(homePlayers, home1 ? [home1.id] : []);
    const away1 = getRandomPlayer(
      awayPlayers,
      [home1?.id, home2?.id].filter(Boolean) as number[]
    );
    const away2 = getRandomPlayer(
      awayPlayers,
      [home1?.id, home2?.id, away1?.id].filter(Boolean) as number[]
    );
    return {
      home_player_1: home1,
      home_player_2: home2,
      away_player_1: away1,
      away_player_2: away2,
    };
  });
}

// Populates all player selects and 5 out of 6 leg inputs for every match
export function generateRandomMatchInputs(
  playerMatches: PlayerMatch[],
  playerDoubleMatches: PlayerDoubleMatch[],
  homePlayers: Player[],
  awayPlayers: Player[]
): {
  homeInputs: { [matchId: number]: string[] };
  awayInputs: { [matchId: number]: string[] };
  singlesAssignments: { home: Player | null; away: Player | null }[];
  doublesAssignments: Array<{
    home_player_1: Player | null;
    home_player_2: Player | null;
    away_player_1: Player | null;
    away_player_2: Player | null;
  }>;
} {
  // Assign players
  const singlesAssignments = generateRandomSingles(playerMatches, homePlayers, awayPlayers);
  const doublesAssignments = generateRandomDoubles(playerDoubleMatches, homePlayers, awayPlayers);

  // Helper to fill 5 out of 6 legs (3 home, 3 away)
  function fillFiveLegs(): [string[], string[]] {
    // Generate 6 random values (9-100 as string)
    const values = Array.from({ length: 6 }, () => String(Math.floor(Math.random() * (100 - 9 + 1)) + 9));
    // Randomly pick one index to blank
    const blankIdx = Math.floor(Math.random() * 6);
    const result = values.map((v, i) => (i === blankIdx ? "" : v));
    return [result.slice(0, 3), result.slice(3, 6)];
  }

  // Build inputs for singles
  const homeInputs: { [matchId: number]: string[] } = {};
  const awayInputs: { [matchId: number]: string[] } = {};
  playerMatches.forEach((pm) => {
    const [homeLegs, awayLegs] = fillFiveLegs();
    homeInputs[pm.id] = homeLegs;
    awayInputs[pm.id] = awayLegs;
  });
  // Build inputs for doubles
  playerDoubleMatches.forEach((dm) => {
    const [homeLegs, awayLegs] = fillFiveLegs();
    homeInputs[dm.id] = homeLegs;
    awayInputs[dm.id] = awayLegs;
  });

  return { homeInputs, awayInputs, singlesAssignments, doublesAssignments };
}
